import { formatCents, formatCount, formatPercent, formatEpc } from '@affily/shared/money';
import { CURRENCY_LABELS } from '@affily/shared/constants';
import { Unknown } from './ui.jsx';

/**
 * The dashboard metric row.
 *
 * `value` of null is the honest case and renders as a dash with an explanation
 * — the API returns null for EPC and conversion rate whenever there were no
 * clicks to divide by. No component in this file will render a 0% that wasn't
 * actually measured.
 */
export function MetricRow({ metrics, className = '' }) {
  return (
    <div
      className={`grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-3 lg:grid-cols-6 ${className}`}
    >
      {metrics.map((m) => (
        <div key={m.label} className="bg-paper-raised px-4 py-3">
          <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
            {m.label}
          </p>
          <p className="mt-1 text-xl font-semibold tabular-nums text-ink">
            {m.value ?? <Unknown reason={m.empty ?? 'Nothing measured yet'} />}
          </p>
          {m.hint && <p className="mt-0.5 text-xs text-ink-faint">{m.hint}</p>}
        </div>
      ))}
    </div>
  );
}

/**
 * A money figure in every currency it exists in: "€1,200.00 · $300.00".
 * Amounts in different currencies are never added together.
 */
function perCurrency(totals, money, pick, format = formatCents) {
  const rows = money?.length > 1 ? money : [{ currency: totals.currency, totals }];
  const values = rows.map((m) => {
    const v = pick(m.totals);
    return v === null || v === undefined ? null : format(v, { currency: m.currency ?? 'usd' });
  });
  return values.every((v) => v === null) ? null : values.filter(Boolean).join(' · ');
}

/** Standard metric set, derived identically for both sides of the marketplace. */
export function performanceMetrics(totals, money = []) {
  return [
    {
      label: 'Unique clicks',
      value: formatCount(totals.uniqueClicks),
      hint: `${formatCount(totals.totalClicks)} total`,
    },
    { label: 'Conversions', value: formatCount(totals.conversions) },
    {
      label: 'Conversion rate',
      value: totals.conversionRatePct === null ? null : formatPercent(totals.conversionRatePct),
      empty: 'No clicks yet, so there is no rate to report',
    },
    {
      label: 'EPC',
      value: perCurrency(totals, money, (t) => t.epc, formatEpc),
      empty: 'No clicks yet, so there is nothing to divide by',
    },
    { label: 'Tracked revenue', value: perCurrency(totals, money, (t) => t.revenueCents) },
    {
      label: 'AOV',
      value: perCurrency(totals, money, (t) => t.aovCents),
      empty: 'No conversions yet',
    },
  ];
}

/**
 * Commission money, split by stage.
 *
 * Shown as separate figures rather than one "earnings" number: accrued, ready
 * to pay, and actually paid are genuinely different things to a creator
 * deciding whether they can pay rent.
 *
 * Each side sees its own half of Affily's fee: taken out of the creator's
 * commission, added on top of what the brand pays.
 */
export function MoneyBreakdown({ commissions, currency = commissions?.currency, side = 'creator', className = '' }) {
  const brand = side === 'brand';
  const money = (cents) => formatCents(cents, { currency });
  const stages = [
    { key: 'pending', label: 'Accrued', hint: 'Tracked, not yet confirmed by the brand' },
    { key: 'approved', label: 'Approved', hint: 'Confirmed, awaiting settlement' },
    { key: 'settled', label: 'Ready to pay', hint: 'Settled by the brand, queued for payout' },
    { key: 'paid', label: 'Paid out', hint: 'Transferred to your account' },
    { key: 'reversed', label: 'Reversed', hint: 'Refunded or cancelled orders' },
  ];

  return (
    <div className={`overflow-x-auto rounded-lg border border-rule bg-paper-raised ${className}`}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Stage</th>
            <th className="num">Entries</th>
            <th className="num">{brand ? 'Commission' : 'Gross'}</th>
            <th className="num">Affily fee</th>
            <th className="num">{brand ? 'You pay' : 'Net to you'}</th>
          </tr>
        </thead>
        <tbody>
          {stages.map((s) => {
            const row = commissions?.[s.key] ?? { count: 0, grossCents: 0, feeCents: 0, brandFeeCents: 0, netCents: 0 };
            const fee = brand ? (row.brandFeeCents ?? 0) : row.feeCents;
            return (
              <tr key={s.key} className={s.key === 'reversed' && row.count === 0 ? 'opacity-50' : ''}>
                <td>
                  <span className="font-medium">{s.label}</span>
                  <span className="block text-xs text-ink-faint">{s.hint}</span>
                </td>
                <td className="num">{formatCount(row.count)}</td>
                <td className="num">{money(row.grossCents)}</td>
                <td className="num text-ink-muted">
                  {fee ? `${brand ? '+' : '−'}${money(fee)}` : money(0)}
                </td>
                <td className="num font-medium">{money(brand ? row.grossCents + fee : row.netCents)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** MoneyBreakdown once per currency, headed, when there is more than one. */
export function MoneyByCurrency({ commissions, money = [], side = 'creator', className = '' }) {
  if (money.length <= 1) return <MoneyBreakdown commissions={commissions} side={side} className={className} />;
  return (
    <div className={`space-y-4 ${className}`}>
      {money.map((m) => (
        <div key={m.currency}>
          <p className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-faint">
            {CURRENCY_LABELS[m.currency] ?? m.currency.toUpperCase()}
          </p>
          <MoneyBreakdown commissions={m.commissions} currency={m.currency} side={side} />
        </div>
      ))}
    </div>
  );
}
